import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Target, Copy, CheckCircle2, Users, MapPin, TrendingUp, Lock, Sparkles, Palette, Save, Rocket, Trash2 } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import PaywallPopup from './PaywallPopup';

// LocalStorage key for persisting Targeting results
const TARGETING_STORAGE_KEY = 'finads_targeting_data';

const TargetingHelper = () => {
  const [productType, setProductType] = useState('');
  const [audienceDescription, setAudienceDescription] = useState('');
  const [region, setRegion] = useState('deutschland');
  const [isGenerating, setIsGenerating] = useState(false);
  const [targetingResults, setTargetingResults] = useState(null);
  const [copied, setCopied] = useState({});
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Load persisted data on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(TARGETING_STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.productType) setProductType(parsed.productType);
        if (parsed.audienceDescription) setAudienceDescription(parsed.audienceDescription);
        if (parsed.region) setRegion(parsed.region);
        if (parsed.targetingResults) setTargetingResults(parsed.targetingResults);
        if (parsed.isSaved) setIsSaved(parsed.isSaved);
      }
    } catch (e) {
      console.error('Error loading saved Targeting data:', e);
    }
  }, []);

  // Save to localStorage whenever targeting results change
  useEffect(() => {
    if (targetingResults) {
      try {
        localStorage.setItem(TARGETING_STORAGE_KEY, JSON.stringify({
          productType,
          audienceDescription,
          region,
          targetingResults,
          isSaved,
          savedAt: new Date().toISOString()
        }));
      } catch (e) {
        console.error('Error saving Targeting data:', e);
      }
    }
  }, [targetingResults, productType, audienceDescription, region, isSaved]);

  // Clear saved data
  const clearSavedData = () => {
    localStorage.removeItem(TARGETING_STORAGE_KEY);
    setTargetingResults(null);
    setProductType('');
    setAudienceDescription('');
    setIsSaved(false);
    toast.success('Gespeicherte Targeting-Daten gelöscht');
  };

  useEffect(() => {
    checkUnlockStatus();
  }, []);

  const checkUnlockStatus = async () => {
    try {
      const response = await axiosInstance.get('/targeting/status');
      setIsUnlocked(response.data.unlocked);
    } catch (error) {
      console.error('Error checking unlock status:', error);
      setIsUnlocked(false);
    } finally {
      setCheckingStatus(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-12 h-12 animate-spin text-[#A78BFA]" />
      </div>
    );
  }

  // Show paywall immediately if not unlocked
  if (!isUnlocked) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-4 border-[rgba(167,139,250,0.3)] shadow-2xl">
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-purple-100 w-24 h-24 rounded-full flex items-center justify-center">
                  <Lock className="w-14 h-14 text-[#A78BFA]" />
                </div>
              </div>
              
              <h2 className="text-4xl font-extrabold text-[#F5F5F7] mb-4">
                🎯 Targeting Helper freischalten
              </h2>
              
              <p className="text-xl text-[rgba(245,245,247,0.9)] mb-8">
                Erhalte Zugriff auf präzise Meta Ads Targeting-Empfehlungen für deine Finanzprodukte
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8 text-left max-w-2xl mx-auto">
                <div className="flex items-start gap-3 p-4 bg-[rgba(167,139,250,0.1)] rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-[#A78BFA] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#F5F5F7]">Detaillierte Interessen-Kategorien</p>
                    <p className="text-sm text-[rgba(245,245,247,0.7)]">Über 50+ präzise Targeting-Vorschläge</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[rgba(167,139,250,0.1)] rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-[#A78BFA] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#F5F5F7]">Demografische Zielgruppen</p>
                    <p className="text-sm text-[rgba(245,245,247,0.7)]">Alter, Geschlecht, Lebenssituation</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[rgba(167,139,250,0.1)] rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-[#A78BFA] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#F5F5F7]">Verhaltensbasiertes Targeting</p>
                    <p className="text-sm text-[rgba(245,245,247,0.7)]">Finanzverhalten & Kaufabsichten</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[rgba(167,139,250,0.1)] rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-[#A78BFA] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#F5F5F7]">Meta Ads Manager Ready</p>
                    <p className="text-sm text-[rgba(245,245,247,0.7)]">Direkt verwendbar in deinen Kampagnen</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-6 mb-8 max-w-md mx-auto" style={{ backgroundColor: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                <p className="text-sm mb-2" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Einmalzahlung</p>
                <p className="text-5xl font-extrabold mb-2" style={{ color: '#A78BFA' }}>59€</p>
                <p style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Lebenslanger Zugriff</p>
              </div>

              <Button
                onClick={() => setShowPaywall(true)}
                className="h-14 px-12 text-lg font-bold"
                style={{
                  background: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
                  color: '#0B0E14'
                }}
              >
                Jetzt freischalten & loslegen →
              </Button>

              <p className="text-sm text-[rgba(245,245,247,0.6)] mt-6">
                ✓ Einmalige Zahlung • ✓ Keine Folgekosten • ✓ Sofortiger Zugriff
              </p>
            </div>
          </CardContent>
        </Card>

        <PaywallPopup
          type="targeting"
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
        />
      </div>
    );
  }

  const products = [
    { value: 'bu', label: 'Berufsunfähigkeitsversicherung' },
    { value: 'rente', label: 'Klassische Rentenversicherung' },
    { value: 'leben', label: 'Lebensversicherung' },
    { value: 'bav', label: 'Betriebliche Altersvorsorge (bAV)' },
    { value: 'kind', label: 'Kindersparvertrag' },
    { value: 'pkv', label: 'Krankenvollversicherung (PKV)' },
    { value: 'zahn', label: 'Zahnzusatzversicherung' },
    { value: 'heilpraktiker', label: 'Heilpraktikerversicherung' },
    { value: 'brille', label: 'Brillenversicherung' },
    { value: 'beamte', label: 'Beamtentarife / Freie Heilfürsorge' },
    { value: 'kfz', label: 'Kfz-Versicherung' },
    { value: 'haftpflicht', label: 'Haftpflichtversicherung' },
    { value: 'hausrat', label: 'Hausrat/Gebäude' },
    { value: 'unfall', label: 'Unfallversicherung' },
    { value: 'privathaft', label: 'Privathaftpflicht' },
    { value: 'rechts_privat', label: 'Rechtsschutz Privat' },
    { value: 'rechts_gewerbe', label: 'Rechtsschutz Gewerblich' },
    { value: 'gold', label: 'Goldsparpläne' },
    { value: 'immo_finanz', label: 'Immobilienfinanzierung' },
    { value: 'immo_kapital', label: 'Immobilien als Kapitalanlage' }
  ];

  const regions = [
    { value: 'deutschland', label: 'Deutschland' },
    { value: 'österreich', label: 'Österreich' },
    { value: 'schweiz', label: 'Schweiz' },
    { value: 'dach', label: 'DACH-Region' }
  ];

  const handleGenerate = async () => {
    if (!productType || !audienceDescription.trim()) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setIsGenerating(true);
    setTargetingResults(null);
    setIsSaved(false); // Reset saved state for new generation

    try {
      const response = await axiosInstance.post('/targeting/generate', {
        product_type: productType,
        audience_description: audienceDescription,
        region: region
      });

      // Update unlock status from response
      if (response.data.is_unlocked !== undefined) {
        setIsUnlocked(response.data.is_unlocked);
      }

      setTargetingResults(response.data);
      toast.success('Targeting-Vorschläge erfolgreich generiert!');
      
      // Notify other components that new content was created
      window.dispatchEvent(new CustomEvent('contentCreated', { detail: { type: 'targeting' } }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('In Zwischenablage kopiert!');
    setTimeout(() => {
      setCopied({ ...copied, [key]: false });
    }, 2000);
  };

  // Save targeting for Meta Ads Autopilot
  const saveTargetingForAutopilot = async () => {
    if (!targetingResults) {
      toast.error('Bitte generieren Sie zuerst Targeting-Vorschläge');
      return;
    }

    setIsSaving(true);
    try {
      // Prepare targeting data structure for Autopilot
      const targetingData = {
        product_type: productType,
        region: region,
        audience_description: audienceDescription,
        generated_at: new Date().toISOString(),
        // Include all targeting results
        summary: targetingResults.summary,
        scenario_1_with_special_category: targetingResults.scenario_1_with_special_category,
        scenario_2_without_special_category: targetingResults.scenario_2_without_special_category,
        andromeda_strategy: targetingResults.andromeda_strategy,
        creative_diversity_recommendations: targetingResults.creative_diversity_recommendations,
        audience_signals: targetingResults.audience_signals,
        first_party_options: targetingResults.first_party_options,
        what_NOT_to_do: targetingResults.what_NOT_to_do,
        advantage_plus_setup: targetingResults.advantage_plus_setup,
        metrics_to_track: targetingResults.metrics_to_track,
        compliance_notes: targetingResults.compliance_notes,
        tips: targetingResults.tips
      };

      await axiosInstance.post('/targeting/save', targetingData);
      setIsSaved(true);
      toast.success('Targeting für Meta Ads Autopilot gespeichert! 🎯', {
        description: 'Sie können dieses Targeting jetzt im Autopilot-Wizard verwenden.'
      });
      
      // Notify Meta Ads Autopilot to refresh targeting
      window.dispatchEvent(new CustomEvent('contentCreated', { detail: { type: 'targeting' } }));
    } catch (error) {
      toast.error('Fehler beim Speichern des Targetings');
      console.error('Save targeting error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="targeting-helper">
      <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)] relative">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Target className="w-6 h-6 text-[#A78BFA]" />
            Meta Ads Targeting-Hilfe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[rgba(245,245,247,0.7)] mb-6">
            Beschreiben Sie Ihre Zielgruppe und erhalten Sie KI-gestützte Targeting-Vorschläge für Meta Ads Manager.
          </p>

          <div className="space-y-6">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label htmlFor="product-type">Produkt / Versicherung</Label>
              <Select value={productType} onValueChange={setProductType}>
                <SelectTrigger id="product-type">
                  <SelectValue placeholder="Wählen Sie ein Produkt..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {products.map((product) => (
                    <SelectItem key={product.value} value={product.value}>
                      {product.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Region Selection */}
            <div className="space-y-2">
              <Label htmlFor="region">Zielregion</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger id="region">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Audience Description */}
            <div className="space-y-2">
              <Label htmlFor="audience-description">Beschreibung Ihrer Zielgruppe</Label>
              <Textarea
                id="audience-description"
                placeholder="z.B. Selbstständige Handwerker zwischen 30-50 Jahren, die ihr Einkommen absichern möchten. Mittleres bis hohes Einkommen, hauptsächlich männlich..."
                value={audienceDescription}
                onChange={(e) => setAudienceDescription(e.target.value)}
                rows={5}
                className="resize-none"
              />
              <p className="text-xs text-[rgba(245,245,247,0.6)]">
                Je detaillierter Ihre Beschreibung, desto präziser die Targeting-Vorschläge
              </p>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !productType || !audienceDescription.trim()}
              className="w-full h-12 text-lg font-semibold"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generiere Targeting-Vorschläge...
                </>
              ) : (
                <>
                  <Target className="w-5 h-5 mr-2" />
                  Targeting-Vorschläge generieren
                </>
              )}
            </Button>
          </div>
        </CardContent>

        {/* Data Suction Animation Overlay */}
        {isGenerating && (
          <div className="absolute inset-0 bg-[rgba(255,255,255,0.05)]/95 backdrop-blur-sm flex items-center justify-center rounded-lg z-50">
            <style dangerouslySetInnerHTML={{__html: `
              @keyframes suckIn {
                0% {
                  opacity: 0;
                  transform: translate(var(--start-x), var(--start-y)) scale(1);
                }
                20% {
                  opacity: 1;
                }
                100% {
                  opacity: 0;
                  transform: translate(0, 0) scale(0.1);
                }
              }
              .particle {
                animation: suckIn 3s ease-in-out infinite;
                position: absolute;
                font-size: 1.5rem;
              }
            `}} />
            
            <div className="text-center relative w-full px-8">
              {/* Animated Data Particles flying towards center */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(24)].map((_, i) => {
                  const angle = (i * 360) / 24;
                  const distance = 350;
                  const x = Math.cos(angle * Math.PI / 180) * distance;
                  const y = Math.sin(angle * Math.PI / 180) * distance;
                  const icons = ['📊', '📈', '🎯', '👥', '💡', '🔍', '📱', '💼', '🏆', '⭐', '🚀', '📉'];
                  
                  return (
                    <div
                      key={i}
                      className="particle"
                      style={{
                        '--start-x': `${x}px`,
                        '--start-y': `${y}px`,
                        animationDelay: `${i * 0.125}s`
                      }}
                    >
                      {icons[i % icons.length]}
                    </div>
                  );
                })}
              </div>

              {/* Central Vortex */}
              <div className="relative mx-auto mb-8 w-40 h-40">
                {/* Pulsing outer ring */}
                <div className="absolute inset-0 border-4 border-[rgba(167,139,250,0.3)] rounded-full animate-ping" style={{ animationDuration: '2s' }}></div>
                
                {/* Spinning middle ring */}
                <div className="absolute inset-4 border-4 border-purple-400 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                
                {/* Counter-rotating inner ring */}
                <div className="absolute inset-8 border-4 border-indigo-500 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
                
                {/* Center icon with pulse */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full p-5 shadow-2xl">
                    <Target className="w-14 h-14 text-white animate-pulse" />
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-[#F5F5F7] mb-3 animate-pulse">
                🤖 KI analysiert Ihre Zielgruppe
              </h3>
              <p className="text-[rgba(245,245,247,0.9)] text-lg mb-6">
                <span className="inline-block animate-bounce" style={{ animationDelay: '0s' }}>📡</span>
                <span className="mx-2">Daten werden gesammelt und verarbeitet</span>
                <span className="inline-block animate-bounce" style={{ animationDelay: '0.2s' }}>💫</span>
              </p>
              
              {/* Progress stages */}
              <div className="max-w-md mx-auto space-y-3 text-left bg-[rgba(255,255,255,0.05)]/80 rounded-lg p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[rgba(0,242,255,0.1)]0 rounded-full animate-pulse"></div>
                  <span className="text-[rgba(245,245,247,0.9)] font-medium">Meta Andromeda Algorithmus aktiv</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[rgba(0,242,255,0.1)]0 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                  <span className="text-[rgba(245,245,247,0.9)] font-medium">Zielgruppen-Signale werden analysiert</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                  <span className="text-[rgba(245,245,247,0.9)] font-medium">Creative Diversity Strategien werden erstellt</span>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-[rgba(245,245,247,0.6)] text-sm">⏱️ Geschätzte Wartezeit: 30-60 Sekunden</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-[rgba(167,139,250,0.1)]0 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-[rgba(167,139,250,0.1)]0 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-[rgba(167,139,250,0.1)]0 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Loading Skeleton Cards - shown below the form */}
      {isGenerating && (
        <div className="space-y-6 animate-pulse">
          {/* Loading Summary Card */}
          <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="bg-purple-200 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Loader2 className="w-6 h-6 text-[#A78BFA] animate-spin" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-purple-200 rounded w-3/4"></div>
                  <div className="h-4 bg-purple-100 rounded w-full"></div>
                  <div className="h-4 bg-purple-100 rounded w-5/6"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading Strategy Cards */}
          <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.3)]">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 animate-pulse" />
                <div className="h-6 bg-[rgba(167,139,250,0.1)]0 rounded w-64"></div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg space-y-2">
                <div className="h-4 bg-purple-200 rounded w-full"></div>
                <div className="h-4 bg-purple-200 rounded w-full"></div>
                <div className="h-4 bg-purple-200 rounded w-3/4"></div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-2 border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 animate-pulse" />
                <div className="h-6 bg-[rgba(0,242,255,0.1)]0 rounded w-72"></div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="bg-red-50 border-l-4 border-red-300 p-4">
                <div className="h-4 bg-red-200 rounded w-full"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </CardContent>
          </Card>

          {/* Loading Creative Diversity */}
          <Card className="shadow-lg">
            <CardHeader className="bg-[rgba(0,242,255,0.1)]">
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#00F2FF] animate-pulse" />
                <div className="h-5 bg-green-200 rounded w-48"></div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-2 p-3 bg-[rgba(0,242,255,0.1)] rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-green-200 rounded w-full"></div>
                    <div className="h-3 bg-green-200 rounded w-4/5"></div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

        </div>
      )}

      {/* Results */}
      {targetingResults && !isGenerating && (
        <div className="space-y-6">
          {/* Summary Card with Clear Button */}
          <Card className="shadow-lg border-2 border-[rgba(0,242,255,0.2)] bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-[#00F2FF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#F5F5F7] mb-2">Targeting-Strategie bereit!</h3>
                    <p className="text-[rgba(245,245,247,0.9)]">{targetingResults.summary}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSavedData}
                  className="text-[rgba(245,245,247,0.6)] hover:text-red-500 flex-shrink-0"
                  data-testid="clear-targeting-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Neu erstellen
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save for Autopilot Button */}
          <Card className="shadow-lg border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-100 w-12 h-12 rounded-full flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#F5F5F7]">Meta Ads Autopilot</h4>
                    <p className="text-sm text-[rgba(245,245,247,0.7)]">Verwenden Sie dieses Targeting direkt im Autopilot-Wizard</p>
                  </div>
                </div>
                <Button
                  onClick={saveTargetingForAutopilot}
                  disabled={isSaving || isSaved}
                  className={`h-12 px-6 font-semibold transition-all ${
                    isSaved 
                      ? 'bg-green-600 hover:bg-green-600' 
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                  }`}
                  data-testid="save-targeting-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird gespeichert...
                    </>
                  ) : isSaved ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Gespeichert!
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Für Autopilot speichern
                    </>
                  )}
                </Button>
              </div>
              {isSaved && (
                <div className="mt-4 p-3 bg-green-100 rounded-lg text-sm text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <span>Targeting gespeichert! Gehen Sie zum <strong>Meta Ads Autopilot</strong>, um es in einer neuen Kampagne zu verwenden.</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Important Notice */}
          {targetingResults.important_notice && (
            <Card className="shadow-lg border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">⚠️</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#F5F5F7] mb-2">{targetingResults.important_notice.title}</h3>
                    <p className="text-[rgba(245,245,247,0.9)] mb-3">{targetingResults.important_notice.message}</p>
                    <div className="bg-[rgba(255,255,255,0.05)] p-3 rounded-lg border-l-4 border-yellow-500">
                      <p className="text-sm font-semibold text-[#F5F5F7]">💡 Empfehlung:</p>
                      <p className="text-sm text-[rgba(245,245,247,0.9)] mt-1">{targetingResults.important_notice.recommendation}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scenario 1: WITH Special Category */}
          {targetingResults.scenario_1_with_special_category && (
            <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.3)]">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {targetingResults.scenario_1_with_special_category.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Primary Method</Label>
                    <p className="text-[#F5F5F7] font-medium mt-1">{targetingResults.scenario_1_with_special_category.primary_method}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Location</Label>
                    <p className="text-[#F5F5F7] mt-1">{targetingResults.scenario_1_with_special_category.location}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Alter</Label>
                    <p className="text-[#F5F5F7] mt-1">{targetingResults.scenario_1_with_special_category.age}</p>
                  </div>
                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg">
                    <Label className="text-sm font-semibold text-[#A78BFA]">Creative Approach</Label>
                    <p className="text-[rgba(245,245,247,0.9)] mt-2">{targetingResults.scenario_1_with_special_category.creative_approach}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scenario 2: WITHOUT Special Category */}
          {targetingResults.scenario_2_without_special_category && (
            <Card className="shadow-lg border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  {targetingResults.scenario_2_without_special_category.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {targetingResults.scenario_2_without_special_category.warning && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                    <p className="text-red-800 font-semibold">{targetingResults.scenario_2_without_special_category.warning}</p>
                  </div>
                )}
                
                <div className="space-y-4">
                  {targetingResults.scenario_2_without_special_category.age_targeting && (
                    <div>
                      <Label className="text-sm font-semibold text-blue-600">Alter-Targeting</Label>
                      <p className="text-[#F5F5F7] mt-1">{targetingResults.scenario_2_without_special_category.age_targeting}</p>
                    </div>
                  )}
                  
                  {targetingResults.scenario_2_without_special_category.gender_targeting && (
                    <div>
                      <Label className="text-sm font-semibold text-blue-600">Geschlecht</Label>
                      <p className="text-[#F5F5F7] mt-1">{targetingResults.scenario_2_without_special_category.gender_targeting}</p>
                    </div>
                  )}
                  
                  {targetingResults.scenario_2_without_special_category.detailed_interests && (
                    <div>
                      <Label className="text-sm font-semibold text-blue-600 mb-2 block">Detaillierte Interessen</Label>
                      <div className="space-y-2">
                        {targetingResults.scenario_2_without_special_category.detailed_interests.map((interest, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-2 bg-[rgba(0,242,255,0.1)] rounded-lg">
                            <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-1" />
                            <p className="text-[#F5F5F7] text-sm">{interest}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {targetingResults.scenario_2_without_special_category.behaviors && (
                    <div>
                      <Label className="text-sm font-semibold text-blue-600 mb-2 block">Verhaltensweisen</Label>
                      <div className="flex flex-wrap gap-2">
                        {targetingResults.scenario_2_without_special_category.behaviors.map((behavior, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {behavior}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {targetingResults.scenario_2_without_special_category.demographics && (
                    <div>
                      <Label className="text-sm font-semibold text-blue-600 mb-2 block">Demografische Merkmale</Label>
                      <div className="space-y-2">
                        {targetingResults.scenario_2_without_special_category.demographics.map((demo, idx) => (
                          <div key={idx} className="p-2 bg-[rgba(0,242,255,0.1)] rounded-lg">
                            <p className="text-[#F5F5F7] text-sm">{demo}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Old Andromeda Strategy - kept for backward compatibility */}
          {targetingResults.andromeda_strategy && !targetingResults.scenario_1_with_special_category && (
            <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.3)]">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Meta Andromeda 2025 Strategie
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Primary Method</Label>
                    <p className="text-[#F5F5F7] font-medium mt-1">{targetingResults.andromeda_strategy.primary_method}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Location</Label>
                    <p className="text-[#F5F5F7] mt-1">{targetingResults.andromeda_strategy.location}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#A78BFA]">Alter</Label>
                    <p className="text-[#F5F5F7] mt-1">{targetingResults.andromeda_strategy.age}</p>
                  </div>
                  <div className="bg-[rgba(167,139,250,0.1)] p-4 rounded-lg">
                    <Label className="text-sm font-semibold text-[#A78BFA]">Creative Approach</Label>
                    <p className="text-[rgba(245,245,247,0.9)] mt-2">{targetingResults.andromeda_strategy.creative_approach}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Creative Diversity Recommendations */}
          {targetingResults.creative_diversity_recommendations && (
            <Card className="shadow-lg">
              <CardHeader className="bg-[rgba(0,242,255,0.1)]">
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#00F2FF]" />
                  Creative Diversity (KING!)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-[rgba(245,245,247,0.7)] mb-4">
                  Post-Andromeda ist Creative Diversity der wichtigste Faktor! 8-15 ECHTE Varianten:
                </p>
                <div className="space-y-2">
                  {targetingResults.creative_diversity_recommendations.slice(0, isUnlocked ? undefined : 3).map((creative, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-[rgba(0,242,255,0.1)] rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-1" />
                      <p className="text-[#F5F5F7]">{creative}</p>
                    </div>
                  ))}
                  {!isUnlocked && targetingResults.creative_diversity_recommendations.length > 3 && (
                    <div className="relative">
                      {targetingResults.creative_diversity_recommendations.slice(3, 6).map((creative, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-[rgba(0,242,255,0.1)] rounded-lg blur-sm opacity-50 mb-2">
                          <CheckCircle2 className="w-5 h-5 text-[#00F2FF] flex-shrink-0 mt-1" />
                          <p className="text-[#F5F5F7]">{creative}</p>
                        </div>
                      ))}
                      <div className="absolute inset-0 backdrop-blur-sm bg-[rgba(255,255,255,0.05)]/90 flex items-center justify-center">
                        <Button
                          onClick={() => setShowPaywall(true)}
                          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-xl"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Alle {targetingResults.creative_diversity_recommendations.length} Creatives freischalten
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audience Signals */}
          {targetingResults.audience_signals && (
            <Card className="shadow-lg">
              <CardHeader className="bg-[rgba(0,242,255,0.1)]">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Audience Signals (AI-basiert)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-[rgba(245,245,247,0.7)] mb-4">
                  Diese Signale nutzt Metas Andromeda AI, um die richtige Zielgruppe zu finden:
                </p>
                <div className="flex flex-wrap gap-2">
                  {targetingResults.audience_signals.map((signal, idx) => (
                    <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {signal}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* First Party Options */}
          {targetingResults.first_party_options && targetingResults.first_party_options.length > 0 && (
            <Card className="shadow-lg">
              <CardHeader className="bg-indigo-50">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  First-Party Data Optionen
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-[rgba(245,245,247,0.7)] mb-4">
                  Nutze deine eigenen Daten für besseres Targeting:
                </p>
                <div className="space-y-2">
                  {targetingResults.first_party_options.map((option, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-indigo-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                      <p className="text-[#F5F5F7]">{option}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* What NOT to Do */}
          {targetingResults.what_NOT_to_do && targetingResults.what_NOT_to_do.length > 0 && (
            <Card className="shadow-lg border-2 border-red-100">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <span className="text-2xl">⚠️</span>
                  Was NICHT funktioniert (Andromeda 2025)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  {targetingResults.what_NOT_to_do.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-600 font-bold text-sm">✕</span>
                      </div>
                      <p className="text-[#F5F5F7]">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Advantage+ Setup */}
          {targetingResults.advantage_plus_setup && (
            <Card className="shadow-lg border-2 border-[rgba(167,139,250,0.2)]">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#A78BFA]" />
                  Advantage+ Kampagnen-Setup
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {targetingResults.advantage_plus_setup.campaign_structure && (
                    <div>
                      <Label className="text-sm font-semibold text-[#A78BFA]">Kampagnenstruktur</Label>
                      <p className="text-[#F5F5F7] mt-1">{targetingResults.advantage_plus_setup.campaign_structure}</p>
                    </div>
                  )}
                  {targetingResults.advantage_plus_setup.budget_strategy && (
                    <div>
                      <Label className="text-sm font-semibold text-[#A78BFA]">Budget-Strategie</Label>
                      <p className="text-[#F5F5F7] mt-1">{targetingResults.advantage_plus_setup.budget_strategy}</p>
                    </div>
                  )}
                  {targetingResults.advantage_plus_setup.optimization && (
                    <div>
                      <Label className="text-sm font-semibold text-[#A78BFA]">Optimierung</Label>
                      <p className="text-[#F5F5F7] mt-1">{targetingResults.advantage_plus_setup.optimization}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metrics to Track */}
          {targetingResults.metrics_to_track && targetingResults.metrics_to_track.length > 0 && (
            <Card className="shadow-lg">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                  Wichtige Metriken
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-[rgba(245,245,247,0.7)] mb-4">Diese KPIs solltest du im Auge behalten:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {targetingResults.metrics_to_track.map((metric, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                      <p className="text-[#F5F5F7] font-medium">{metric}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance Notes */}
          {targetingResults.compliance_notes && targetingResults.compliance_notes.length > 0 && (
            <Card className="shadow-lg border-2 border-yellow-100">
              <CardHeader className="bg-yellow-50">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">⚖️</span>
                  Compliance & Rechtliches
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  {targetingResults.compliance_notes.map((note, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                      <p className="text-[#F5F5F7]">{note}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          {targetingResults.tips && targetingResults.tips.length > 0 && (
            <Card className="shadow-lg border-2 border-blue-100 bg-[rgba(0,242,255,0.1)]">
              <CardContent className="pt-6">
                <h4 className="font-bold text-[#F5F5F7] mb-3 text-lg">💡 Tipps für erfolgreiche Kampagnen:</h4>
                <ul className="space-y-3">
                  {targetingResults.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="text-blue-600 text-xl mt-0.5">•</span>
                      <span className="text-[#F5F5F7]">{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Paywall Popup */}
      <PaywallPopup
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type="targeting"
      />
    </div>
  );
};

export default TargetingHelper;
